import { call, put, all, takeLatest } from 'redux-saga/effects'
import { formatContentType } from 'helpers/utils'
import { apiFetchFaq } from 'containers/Faq/services'
import { apiFetchTutorial } from 'containers/Tutorials/services'
import {
  apiFetchFootprints,
  apiUpdateFootprints,
  apiFetchFootprintsSession,
  apiCreateFootprints
} from './services'
import { fetchFootprints, updateFootprints, createFootprints } from './actions'
import {
  CREATE_FOOTPRINTS,
  FETCH_FOOTPRINTS,
  UPDATE_FOOTPRINTS
} from './constants'
import * as KEYS from '../../constants/keys'

function* fetchFootprintsWorker(action) {
  try {
    const customer = sessionStorage.getItem('customer')
    const footprintsSession = yield call(
      apiFetchFootprintsSession,
      action.id,
      customer
    )
    const footprints = yield call(
      apiFetchFootprints,
      footprintsSession.app_instance_id
    )
    footprintsSession.actions = yield call(
      processActions,
      footprints.footprints
    )
    footprintsSession.id = action.id
    yield put(fetchFootprints.success(footprintsSession))
  } catch (e) {
    console.log('Failed to load footprints', e)
    yield put(fetchFootprints.failure(e.message))
  }
}

function* processActions(actions) {
  const faqActions = yield call(extractArticleActions, actions, 'faqs')
  const tutorialActions = yield call(
    extractArticleActions,
    actions,
    'tutorials'
  )

  let results = [
    ...extractDeviceActions(actions),
    ...extractSearchActions(actions),
    ...faqActions,
    ...tutorialActions,
    ...extractFeedbackActions(actions),
    ...extractBotActions(actions)
  ]

  // Sort by timestamp
  results.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

  // Filter out consecutive duplicates
  results = results.filter(
    (item, pos, array) =>
      pos === 0 ||
      item.target !== array[pos - 1].target ||
      item.action !== array[pos - 1].action
  )

  return results
}

function extractDeviceActions(actions) {
  const deviceCategories = ['devicesearch', 'deviceselector']
  const filtered = actions.filter(
    (action) =>
      deviceCategories.includes(action.event_category) === 'deviceselector' &&
      action.action === 'setDevice'
  )

  return filtered.map((action) => ({
    timestamp: action.timestamp.value,
    action: 'Selected Device',
    target: action.device_name
  }))
}

function* extractArticleActions(actions, type) {
  const typeSingular = type.slice(0, -1)
  const isId = (string) => /^\d+$/.test(string)
  const filtered = actions.filter((action) =>
    type === 'tutorials'
      ? action.event_category === 'tutorials' && isId(action.target)
      : action.event_category !== 'tutorials' && isId(action.target)
  )
  let ids = filtered.map((action) => parseInt(action.target, 10))
  ids = [...new Set(ids)]

  const articles = yield call(
    type === 'tutorials' ? fetchTutorials : fetchFaqs,
    ids
  ) || []

  return filtered.map((action) => {
    let stepsCompleted = null
    let completed = null
    const id = action.target
    const article = articles.find(
      (item) => parseInt(item.pk || item.PK, 10) === parseInt(id, 10)
    )
    let linkTo = `/${typeSingular}/${id}`

    if (action.device_id) {
      linkTo = `${linkTo}?device=${action.device_id}`
    }

    let description = `Viewed ${formatContentType(type)}`

    if (type === 'tutorials') {
      stepsCompleted = getStepsCompleted(id, actions)
      description = `${description} (steps: ${stepsCompleted} of ${article.steps_count})`
      completed = stepsCompleted === article.steps_count
    }

    return {
      timestamp: action.timestamp.value,
      action: description,
      target: article && article.name,
      linkTo,
      type: typeSingular,
      completed
    }
  })
}

function getStepsCompleted(id, actions) {
  const matchActions = [
    'OnDeviceClick',
    'OnStepTextClick',
    'ControlPlayAll',
    'ControlPlayNext'
  ]

  const steps = actions.filter(
    (action) =>
      matchActions.some((type) => type === action.action) &&
      parseInt(action.target, 10) === parseInt(id, 10)
  )

  if (steps.length === 0) {
    return 1
  }
  const playedAll = steps.find((step) => step.action === 'ControlPlayAll')

  if (playedAll) {
    return playedAll.step_to_pretty_of
  }

  return Math.max(
    ...steps.map((step) => (step && step.step_to_pretty_step) || 0)
  )
}

function extractSearchActions(actions) {
  const searchActions = ['submit', 'success', 'abandon', 'select_suggestion']
  let filtered = actions.filter(
    (action) =>
      action.event_category === 'search' &&
      searchActions.includes(action.action)
  )

  filtered = filtered.filter((action) => action.search_term.trim() !== '')

  return filtered.map((action) => ({
    timestamp: action.timestamp.value,
    action: 'Searched',
    target: action.search_term,
    linkTo: `/search?q=${action.search_term}`
  }))
}

function extractFeedbackActions(actions) {
  const feedbackCategories = ['faqs', 'tutorials', 'landing']
  const feedbackActions = ['QuickFeedback', 'SurveyFeedback', 'Feedback']
  const filtered = actions.filter(
    (action) =>
      feedbackCategories.includes(action.event_category) &&
      feedbackActions.includes(action.action)
  )

  return filtered.map((action) => {
    const helpful = !!action.feedback_helpful || !!action.helpful

    return {
      timestamp: action.timestamp.value,
      action: 'Left Feedback',
      target: helpful ? 'helpful' : 'not helpful',
      type: 'feedback'
    }
  })
}

function extractBotActions(actions) {
  const filtered = actions.filter(
    (action) =>
      // Bot events have botName property
      action.hasOwnProperty('botName') &&
      !['immediateNext', 'bot start', ' '].includes(action.reply) &&
      action.direction === 'in'
  )

  return filtered.map((action) => {
    const stripHtml = /(<([^>]+)>)/gi
    const reply = action.reply.replace(stripHtml, '')
    return {
      timestamp: action.timestamp.value,
      action: 'Bot interaction:',
      target: reply,
      type: 'botInteraction'
    }
  })
}

function* catchArticleError(fn) {
  // returns empty object if article is missing in DB.
  try {
    return yield fn
  } catch (e) {
    console.log(e, 'Article is not available')
    return {}
  }
}

function* fetchFaqs(ids) {
  return yield all(ids.map((id) => catchArticleError(call(apiFetchFaq, id))))
}

function* fetchTutorials(ids) {
  return yield all(
    ids.map((id) => catchArticleError(call(apiFetchTutorial, id)))
  )
}

function* updateFootprintsWorker(action) {
  try {
    yield call(apiUpdateFootprints, action.id, action.data)
    yield put(updateFootprints.success(action.data))
  } catch (e) {
    yield put(updateFootprints.failure(e.message))
  }
}

function* createFootprintsWorker(action) {
  const customer = sessionStorage.getItem('customer')
  const appInstanceId = sessionStorage.getItem(KEYS.APP_INSTANCE_ID)
  const payload = {
    clientId: action.clientId,
    appInstanceId: appInstanceId,
    customer: customer
  }
  try {
    yield call(apiCreateFootprints, payload)
    yield put(createFootprints.success())
  } catch (e) {
    yield put(createFootprints.failure(e.message))
  }
}

export default function* watcher() {
  yield takeLatest(FETCH_FOOTPRINTS.REQUEST, fetchFootprintsWorker)
  yield takeLatest(UPDATE_FOOTPRINTS.REQUEST, updateFootprintsWorker)
  yield takeLatest(CREATE_FOOTPRINTS.REQUEST, createFootprintsWorker)
}
